<template>
  <div>
    <simpletoolbar />

    <!-- Not found -->
    <div v-if="!useCase" class="max-w-3xl mx-auto px-4 py-20 text-center text-stone-500 dark:text-stone-400">
      <p class="text-2xl mb-4">Page not found</p>
      <router-link to="/" class="text-primary-600 dark:text-primary-400 hover:underline">← Back to Home</router-link>
    </div>

    <template v-else>
      <!-- Hero -->
      <section class="bg-gradient-to-b from-primary-50 to-white dark:from-stone-900 dark:to-stone-800 py-16 px-4">
        <div class="max-w-3xl mx-auto text-center">
          <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 mb-6">
            <i :class="['fas text-3xl text-primary-600 dark:text-primary-400', useCase.heroIcon]"></i>
          </div>
          <h1 class="text-4xl font-bold text-stone-800 dark:text-stone-100 mb-4 leading-tight">
            {{ useCase.headline }}
          </h1>
          <p class="text-xl text-stone-500 dark:text-stone-400 mb-8 max-w-2xl mx-auto">
            {{ useCase.subheadline }}
          </p>
          <router-link
            to="/"
            class="inline-block bg-primary-600 hover:bg-primary-700 dark:hover:bg-primary-800 text-white dark:text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors shadow-md"
          >
            Try It Free →
          </router-link>
        </div>
      </section>

      <!-- Problem -->
      <section class="max-w-3xl mx-auto px-4 py-14">
        <h2 class="text-2xl font-bold text-stone-800 dark:text-stone-100 mb-4">
          {{ useCase.problem.heading }}
        </h2>
        <div class="text-stone-600 dark:text-stone-300 leading-relaxed space-y-4">
          <p v-for="(para, i) in problemParagraphs" :key="i">{{ para }}</p>
        </div>
      </section>

      <!-- How it works -->
      <section class="bg-stone-50 dark:bg-stone-900 py-14 px-4">
        <div class="max-w-3xl mx-auto">
          <h2 class="text-2xl font-bold text-stone-800 dark:text-stone-100 mb-10 text-center">
            How It Works
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div v-for="(step, i) in useCase.steps" :key="i" class="text-center">
              <div class="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900 mb-4">
                <i :class="['fas text-xl text-primary-600 dark:text-primary-400', step.icon]"></i>
              </div>
              <div class="text-sm font-semibold text-primary-600 dark:text-primary-400 mb-1">Step {{ i + 1 }}</div>
              <h3 class="font-bold text-stone-800 dark:text-stone-100 mb-2">{{ step.heading }}</h3>
              <p class="text-stone-500 dark:text-stone-400 text-sm leading-relaxed">{{ step.body }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Use cases -->
      <section class="max-w-3xl mx-auto px-4 py-14">
        <h2 class="text-2xl font-bold text-stone-800 dark:text-stone-100 mb-8 text-center">
          Ways to Use It
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div
            v-for="(usage, i) in useCase.usages"
            :key="i"
            class="flex gap-4 p-5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800"
          >
            <div class="flex-shrink-0 w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900 flex items-center justify-center">
              <i :class="['fas text-primary-600 dark:text-primary-400', usage.icon]"></i>
            </div>
            <div>
              <h3 class="font-semibold text-stone-800 dark:text-stone-100 mb-1">{{ usage.title }}</h3>
              <p class="text-stone-500 dark:text-stone-400 text-sm leading-relaxed">{{ usage.body }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Pro tip -->
      <section class="max-w-3xl mx-auto px-4 pb-10">
        <div class="bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700 rounded-xl p-6 flex gap-4">
          <i class="fas fa-lightbulb text-primary-500 text-xl flex-shrink-0 mt-0.5"></i>
          <div>
            <div class="font-semibold text-primary-700 dark:text-primary-300 mb-1">Pro Tip</div>
            <p class="text-stone-600 dark:text-stone-300 text-sm leading-relaxed">{{ useCase.tip }}</p>
          </div>
        </div>
      </section>

      <!-- CTA -->
      <section class="bg-primary-600 dark:bg-primary-700 py-16 px-4 text-center">
        <h2 class="text-3xl font-bold text-white mb-3">{{ useCase.cta.heading }}</h2>
        <p class="text-primary-100 mb-8 text-lg">{{ useCase.cta.body }}</p>
        <router-link
          to="/"
          class="inline-block bg-white hover:bg-primary-50 text-primary-700 font-semibold px-8 py-4 rounded-lg text-lg transition-colors shadow-md"
        >
          Start Spinning Free →
        </router-link>
      </section>
    </template>

    <app-footer />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import simpletoolbar from '@components/ui/simpletoolbar.vue';
import appFooter from '@components/ui/appFooter.vue';
import { useSeo } from '@/composables/useSeo';
import { getUseCase } from '@/content/useCases.js';

const route = useRoute();
const useCase = computed(() => getUseCase(route.params.slug));

// Split problem body into paragraphs on double newlines
const problemParagraphs = computed(() =>
  useCase.value?.problem.body.split('\n\n').map(p => p.trim()).filter(Boolean) ?? []
);

useSeo({
  title: () => useCase.value ? `${useCase.value.title} | Spin the Names` : 'Spin the Names',
  description: () => useCase.value?.description ?? '',
});
</script>
